'use client';
import React, { useMemo } from 'react';
import {
  Typography,
  Button,
  Card,
  CardHeader,
  Avatar,
  CardMedia,
  SxProps,
  Theme,
  CardContent,
  CardActions,
  Stack,
} from '@mui/material';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { File } from '@peerless/data-access/strapi';
import { formatMediaUrl } from '../utils/formatMediaUrl';
import { RouterLink } from '@/navigation';
import { useTranslations } from 'next-intl';
import { isValidUrl } from '../utils';
import { formatRegionalDate } from '../utils/formatRegionalDate';
import { useCurrentLocale } from '@peerless/ui-locale';

interface PreviewCardProps {
  image?: File;
  title: string;
  description: string;
  externalUrlLink?: string;
  pageUrlLink: string;
  internalDocumentLink?: File;
  urlLabel?: string;
  sx?: SxProps<Theme>;
  icon: FontAwesomeIconProps['icon'];
  date?: string;
  hideDate?: boolean;
  subHeader?: string;
}

export const PreviewCard: React.FC<PreviewCardProps> = ({
  image,
  title,
  description,
  externalUrlLink,
  internalDocumentLink,
  pageUrlLink,
  urlLabel,
  sx,
  icon,
  date,
  subHeader,
  hideDate = false, // Default to false if not provided
}) => {
  const t = useTranslations();
  const { locale } = useCurrentLocale();
  const localizedDate = useMemo(() => {
    if (!date) return null;
    return formatRegionalDate(date, locale);
  }, [date, locale]);
  const mediaUrl = internalDocumentLink ? formatMediaUrl(internalDocumentLink) : pageUrlLink;
  const url = externalUrlLink ?? mediaUrl;
  const target = isValidUrl(url) ? '_blank' : '_self';
  return (
    <Card sx={sx} variant="cardBorder">
      <CardHeader
        sx={{ display: 'none' }}
        avatar={
          <Avatar>
            <FontAwesomeIcon icon={icon} />
          </Avatar>
        }
      />
      {image && <CardMedia component={'img'} alt={title} image={formatMediaUrl(image)} />}
      <CardContent sx={{ paddingBottom: '0' }}>
        <Typography variant={'h3'} component={'div'} marginBottom={0}>
          {title}
        </Typography>
        <Stack>
          {subHeader ? (
            <Typography sx={{ fontWeight: 'bold' }} component={'div'}>
              {subHeader}
            </Typography>
          ) : null}
          {!hideDate && localizedDate ? ( // Conditionally render the date
            <Typography component={'div'}>{localizedDate}</Typography>
          ) : null}
        </Stack>
        {description && <Typography paddingTop={2}>{description}</Typography>}
      </CardContent>
      <CardActions>
        <Button fullWidth variant="outlined" href={url} target={target} component={RouterLink}>
          {urlLabel ?? t('Search.link-here')}
        </Button>
      </CardActions>
    </Card>
  );
};
